import { Box, Flex, HStack, Text, Input, Select, IconButton, Button } from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { SortablePlaylistList, type BlockPlaylist } from './SortablePlaylistList';
import type { PlaybackMode } from '../types';

export interface EditBlock {
  key: string;
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  playbackMode: PlaybackMode | null;
  playlists: BlockPlaylist[];
}

interface TimeBlockEditorProps {
  block: EditBlock;
  onChangeTimes: (start: string, end: string) => void;
  onChangePlaybackMode: (mode: PlaybackMode | null) => void;
  onReorderPlaylists: (orderedPlaylistIds: string[]) => void;
  onRemovePlaylist: (playlistId: string) => void;
  onAddPlaylists: () => void;
  onDelete: () => void;
}

export function TimeBlockEditor({
  block,
  onChangeTimes,
  onChangePlaybackMode,
  onReorderPlaylists,
  onRemovePlaylist,
  onAddPlaylists,
  onDelete,
}: TimeBlockEditorProps) {
  const { t } = useTranslation();

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} bg="gray.50">
      <Flex justify="space-between" align="center" mb={3} gap={3} wrap="wrap">
        <HStack>
          <Input
            type="time"
            value={block.startTime}
            onChange={(e) => onChangeTimes(e.target.value, block.endTime)}
            w="130px"
            bg="white"
          />
          <Text color="gray.400">–</Text>
          <Input
            type="time"
            value={block.endTime}
            onChange={(e) => onChangeTimes(block.startTime, e.target.value)}
            w="130px"
            bg="white"
          />
        </HStack>
        <HStack>
          <Select
            size="sm"
            value={block.playbackMode ?? 'inherit'}
            onChange={(e) => onChangePlaybackMode(e.target.value === 'inherit' ? null : (e.target.value as PlaybackMode))}
            w="auto"
            bg="white"
          >
            <option value="inherit">{t('scenarios.inherit')}</option>
            <option value="sequential">{t('scenarios.playbackModes.sequential')}</option>
            <option value="random">{t('scenarios.playbackModes.random')}</option>
          </Select>
          <IconButton
            aria-label={t('scenarios.deleteBlock')}
            icon={<Text fontSize="sm">✕</Text>}
            size="sm"
            variant="ghost"
            color="gray.400"
            _hover={{ color: 'red.500', bg: 'red.50' }}
            onClick={onDelete}
          />
        </HStack>
      </Flex>

      {block.playlists.length === 0 ? (
        <Text fontSize="sm" color="gray.400" py={2}>
          {t('scenarios.noPlaylistsInBlock')}
        </Text>
      ) : (
        <SortablePlaylistList
          playlists={block.playlists}
          onReorder={onReorderPlaylists}
          onRemove={onRemovePlaylist}
        />
      )}

      <Button size="sm" variant="ghost" colorScheme="brand" leftIcon={<AddIcon />} mt={3} onClick={onAddPlaylists}>
        {t('scenarios.addPlaylists')}
      </Button>
    </Box>
  );
}

export default TimeBlockEditor;
